import { Link, useParams } from "react-router-dom";
import { Building2, PencilLine, TriangleAlert } from "lucide-react";

import { groupMissing } from "./izjavaFields";

interface IzjavaMissingBarProps {
  /** Raw variable names the backend flagged as unfilled for this izjava. */
  missing: string[];
}

/**
 * The "nedostaje" bar shown above an izjava whose template still has unfilled
 * fields. It names the missing data in plain Croatian and routes the fix to the
 * right place — office identity to Postavke (entered once), project identity to
 * Donesi. Replaces the silent wall of "[ ZA RUČNI UNOS ]" markers with an
 * explicit, actionable summary.
 */
export function IzjavaMissingBar({ missing }: IzjavaMissingBarProps) {
  const { projectId } = useParams();
  const { office, project } = groupMissing(missing);
  if (!office.length && !project.length) return null;

  // Project-identity fields (investitor, broj projekta, čestica, planovi) are
  // edited in the ProjectDataForm on the Provjeri step — an absolute path so it
  // doesn't depend on relative-link resolution from the Preuzmi route.
  const provjeriTo = projectId ? `/project/${projectId}/provjeri` : "/";

  return (
    <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-[0.82rem] text-amber-900">
      <div className="flex items-start gap-2">
        <TriangleAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
        <div className="flex-1 space-y-2">
          <p>
            <span className="font-semibold">Za ovu izjavu nedostaje:</span>{" "}
            {[...office, ...project].join(", ")}.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {office.length > 0 && (
              <Link
                to="/postavke"
                className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-1 font-medium text-amber-800 transition-colors hover:bg-amber-100"
              >
                <Building2 className="h-3.5 w-3.5" />
                Podaci ureda (Postavke)
              </Link>
            )}
            {project.length > 0 && (
              <Link
                to={provjeriTo}
                className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-1 font-medium text-amber-800 transition-colors hover:bg-amber-100"
              >
                <PencilLine className="h-3.5 w-3.5" />
                Dopuni podatke projekta
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
